import { Alert } from "antd";
import { Spin, Typography, Upload } from "antd-v5";
import axios from "axios";
import AuthorisedContainer from "components/AuthorisedContainer";
import message from "components/CustomMessage";
import DeleteButton from "components/DeleteButton";
import { FileIcon } from "components/Icons";
import { PermissionSetMap } from "constants/RBACConstants";
import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import FindingAPI from "restAPI/FindingAPI";
import { hasPermission } from "utils/authService";
import { getErrorMessage } from "utils/errorUtil";
import { removeNonPrintableUnicodeCharacters } from "utils/fileUtils";

import ACButton from "ac-components/ac-button/ACButton";
import AttachmentIcon from "ac-components/ac-icons/AttachmentIcon";
import CSVFileIcon from "ac-components/ac-icons/CSVFileIcon";
import PDFFileIcon from "ac-components/ac-icons/PDFFileIcon";
import PlusIcon from "ac-components/ac-icons/PlusIcon";

import { ATTACHMENT_TOOLS } from "../../Constants";
import { useFindingDetails } from "../FindingDetailsProvider";

type FileType = { csv: React.ReactNode; pdf: React.ReactNode };

const FILE_ICONS: FileType = {
  csv: <CSVFileIcon />,
  pdf: <PDFFileIcon />,
};

export default function Attachments() {
  const { finding, loading, onUpdate } = useFindingDetails() as any;
  const [attachments, setAttachments] = useState<[]>();
  const [attachmentErr, setAttachmentErr] = useState<string>("");
  const [fetchingAttachment, setFetchingAttachment] = useState(false);

  const fetchAttachments = useCallback(async () => {
    if (!finding) {
      return;
    }
    const params = {
      id: finding.toolId,
      productId: finding.product?.id,
      subProductId: finding.appId,
      environment: finding.envName,
      findingId: finding.id,
    };
    try {
      setFetchingAttachment(true);
      const res = await FindingAPI.getAttachments(finding.toolName, params)
        .promise;
      setAttachments(res);
    } catch (e: any) {
      const errMsg = getErrorMessage(e);
      setAttachmentErr(errMsg);
      setAttachments([]);
    } finally {
      setFetchingAttachment(false);
    }
  }, [finding]);

  useEffect(() => {
    if (!loading) {
      if (ATTACHMENT_TOOLS.includes(finding.toolName)) {
        fetchAttachments();
      }
    }
  }, [fetchAttachments, loading, finding]);

  const onAttachmentDelete = useCallback(
    async (e, item) => {
      await FindingAPI.deleteAttachment(finding.id, item.id).promise;
      message.success("Successfully deleted attachment");
      onUpdate(true);
    },
    [finding, onUpdate],
  );

  const attachmentData = useMemo(() => {
    const customAttachments = _.map(finding?.attachments || [], (item) => ({
      ...item,
      isCustomAttachment: true,
    }));
    return [
      ...(attachments instanceof Array ? attachments : []),
      ...customAttachments,
    ];
  }, [attachments, finding]);

  return (
    <Spin spinning={loading || fetchingAttachment}>
      {
        <div className="attachment-item-wrapper">
          {!_.isEmpty(attachmentData) ? (
            _.map(attachmentData, (data) => {
              const fileNameWithExtention = data.isCustomAttachment
                ? data.name
                : data.id;
              const fileNameWithExtentionArr = fileNameWithExtention.split(".");
              const extention =
                fileNameWithExtentionArr.pop() as keyof FileType;
              const fileName = fileNameWithExtentionArr.join(".");
              const icon = FILE_ICONS[extention];

              return (
                <div className="attachment-item">
                  <div className="attachment-icon">{icon || <FileIcon />}</div>
                  <div className="attachment-details">
                    <div
                      className="attachment-name text-ellipsis"
                      title={fileName}
                    >
                      <a
                        rel="noopener noreferrer"
                        href={
                          data.isCustomAttachment
                            ? `user/tools/generic/CUSTOM/attachment?attachmentId=${data.id}&filename=${data.name}`
                            : data.name
                        }
                        target="_blank"
                      >
                        {fileName}
                      </a>
                    </div>
                    <div className="attachment-metadata">{extention}</div>
                  </div>
                  <AuthorisedContainer
                    permission={PermissionSetMap.UPDATE_FINDINGS}
                  >
                    {
                      // @ts-expect-error
                      <DeleteButton
                        className="delete-attachment-icon"
                        onDelete={onAttachmentDelete}
                        data={data}
                        type="icon"
                        confirmText="Delete"
                      />
                    }
                  </AuthorisedContainer>
                </div>
              );
            })
          ) : attachmentErr ? (
            <Alert
              message={attachmentErr}
              type="error"
              className="text-center"
            />
          ) : (
            <Typography.Paragraph
              className="text-center m-b-0"
              type="secondary"
            >
              No Attachments
            </Typography.Paragraph>
          )}
        </div>
      }
    </Spin>
  );
}

export function AttachmentHeader() {
  const { onUpdate, id } = useFindingDetails();
  const [uplodaing, setUploading] = useState(false);

  const onFileChange = useCallback(
    async (e) => {
      const file = e.file;
      if (file) {
        setUploading(true);
        if (file.status === "uploading") {
          return;
        }
        try {
          const { id: attachmentId, signedUrl } =
            await FindingAPI.uploadFindingAttachment(id, {
              fileName: removeNonPrintableUnicodeCharacters(file.name),
            }).promise;
          await axios({ method: "put", url: signedUrl, data: file });
          message.success("Successfully uploaded attachment");
          FindingAPI.uploadFindingAttachmentToTicket(id, {
            attachmentId,
            signedUrl,
          });
          onUpdate(true);
        } catch (e) {
          console.log("upload fidning attachment err", e);
        } finally {
          setUploading(false);
        }
      }
    },
    [id, onUpdate],
  );

  const hasPermissionToUpload = hasPermission(PermissionSetMap.UPDATE_FINDINGS);
  const disableUpload = !hasPermissionToUpload || uplodaing;

  return (
    <div className="d-flex justify-content-between">
      <span className="header-title">
        <AttachmentIcon />
        Attachment
      </span>
      <span onClick={(e) => e.stopPropagation()}>
        <Upload
          multiple={false}
          onChange={onFileChange}
          beforeUpload={() => false}
          showUploadList={false}
          disabled={disableUpload}
        >
          <ACButton
            size="sm"
            type="secondary"
            iconOnly
            loading={uplodaing}
            disabled={disableUpload}
          >
            <PlusIcon />
          </ACButton>
        </Upload>
      </span>
    </div>
  );
}
